import {
  ArrowRightIcon,
  Card,
  XIcon,
  IconButton,
  Image,
  Text,
  UnstyledButton,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { AudienceBadge } from './AudienceBadge'
import type { ChangelogEntryAudience } from './useChangelog'

import styles from './ChangelogNotificationCard.module.css'

import type { JSX } from 'react'

interface ChangelogNotificationCardProps {
  title: string
  coverImageUrl?: string | null
  audience?: ChangelogEntryAudience
  onOpen?: () => void
  onDismiss?: () => void
}

/**
 * Reusable card component for displaying a changelog notification.
 * Shows a cover image (16:9) if available, appropriate badge, and the entry title.
 * Close button appears on hover/focus.
 */
export function ChangelogNotificationCard({
  title,
  coverImageUrl,
  audience,
  onOpen,
  onDismiss,
}: ChangelogNotificationCardProps): JSX.Element {
  return (
    <Card padding="none" className={styles.card}>
      <UnstyledButton onClick={onOpen} className={styles.overlayButton}>
        <VisuallyHidden>Open</VisuallyHidden>
      </UnstyledButton>

      {/* Cover image (16:9) - no badge overlay */}
      {coverImageUrl && (
        <div className={styles.coverImageContainer}>
          <Image src={coverImageUrl} alt="" className={styles.coverImage} />
        </div>
      )}

      {/* Content - badge inline with title */}
      <div className={styles.content}>
        <span className={styles.titleRow}>
          <AudienceBadge
            audience={audience}
            size="small"
            className={styles.badge}
          />
          <Text weight="semibold">{title || 'Untitled'}</Text>
          <ArrowRightIcon size="small" className={styles.arrow} />
        </span>
      </div>

      {/* Close button - tertiary with white background */}
      <IconButton
        variant="tertiary"
        size="small"
        label="Dismiss"
        onClick={(e): void => {
          e.stopPropagation()
          onDismiss?.()
        }}
        className={styles.closeButton}
      >
        <XIcon />
      </IconButton>
    </Card>
  )
}
